/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFetchBaseQuery } from "@/redux/apiFetchBaseQuery";
import {
	CampaignAdvancedReportPdfDownloadResponse,
	CampaignAdvancedReportResponse,
} from "@/types/OrderAdvancedReporting";
import { CopyOrderResponse } from "@/types/AgencyOrder";
import { DefaultApiSuccessResponse } from "@/types/ApiResponse";
import { AutomatedBillingPayload } from "@/domains/AgencyOrder/OrderSetup/Components/automatedBilling";
import type { RtbDeploymentStatusResponse } from "@/types/RtbDeploymentStatus";

const TIMEOUT = 3 * 60 * 1000; // 3 minutes

const buildFormDataPayload = (data: Record<string, unknown>): FormData => {
	const formData = new FormData();

	Object.entries(data).forEach(([key, value]) => {
		formData.append(key, value as string | Blob);
	});

	return formData;
};

export const orderManagementSlice = createApi({
	reducerPath: "orderManagementSlice",
	baseQuery: apiFetchBaseQuery,
	tagTypes: ["orders"],
	endpoints: builder => ({
		getOrderList: builder.query<any, any>({
			query: ({
				page,
				pageSize,
				status,
				startDate,
				endDate,
				agencyFilter,
				orderBy = "o.create_time",
				sortBy = "ASC",
			}) => ({
				url: `agency-order/order-listing/${status}/${startDate}/${endDate}/${agencyFilter}/${orderBy}/${sortBy}`,
				params: {
					page_no: page + 1,
					limit: pageSize,
				},
			}),
			providesTags: ["orders"],
			keepUnusedDataFor: 0, // cache time in seconds
		}),
		getOrderById: builder.query<any, any>({
			query: id => ({
				url: `/agency-order/${id}`,
				method: "GET",
			}),
		}),
		getNotesByOrderID: builder.query<any, any>({
			query: id => ({
				url: `/agency-order/${id}/notes`,
				method: "GET",
			}),
		}),

		isUniqueIO: builder.mutation({
			query: ({ io }) => {
				const formDataPayload = new FormData();

				formDataPayload.append("io", io);
				return {
					url: `agency-order/is-unique-io`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json", // Explicitly set accept header
					},
				};
			},
		}),
		addOrderNote: builder.mutation({
			query: formData => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}
				return {
					url: "agency-order/notes",
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json", // Explicitly set accept header
						// NOTE: Do not set Content-Type here; the browser sets it automatically for FormData
					},
				};
			},
			invalidatesTags: ["orders"],
		}),
		filesUpload: builder.mutation({
			query: formData => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData?.data instanceof FormData ? formData?.data : new FormData();

				// If formData is an object, append its keys to FormData
				if (!(formData instanceof FormData)) {
					Object.entries(formData?.data).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: `agency-order/${formData.orderId}/files`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),
		getProductById: builder.query<any, any>({
			query: ({ id }) => ({
				url: `/agency-order/products/${id}`,
			}),
			providesTags: ["orders"],
		}),
		getMetaById: builder.query<any, any>({
			query: ({ id }) => ({
				url: `/agency-order/order-setup/${id}/meta`,
			}),
		}),
		addCopyCampaign: builder.mutation<CopyOrderResponse, { id: number; io: string }>({
			query: formData => {
				const formDataPayload = formData instanceof FormData ? formData : new FormData();
				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: "agency-order/copy-order",
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),
		deleteOrderNote: builder.mutation({
			query: ({ orderId, noteId }: { orderId: string; noteId: string }) => ({
				url: `agency-order/${orderId}/notes/${noteId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["orders"],
		}),
		rtbAutomation: builder.mutation({
			query: ({ orderId }) => {
				const formDataPayload = new FormData();

				formDataPayload.append("campaign_id", orderId);
				return {
					url: `rtb-automation/deploy-to-report-site`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
					timeout: TIMEOUT,
				};
			},
		}),
		rtbDeployment: builder.mutation({
			query: ({ orderId }) => {
				const formDataPayload = new FormData();
				formDataPayload.append("campaign_id", orderId);

				return {
					url: `rtb-deployment/deploy-campaign`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
					timeout: TIMEOUT,
				};
			},
		}),
		deployNewAds: builder.mutation({
			query: ({
				orderId,
				files,
				removeAndReplaceAll,
			}: {
				orderId: string;
				files: Array<{ file_id: string; file_name: string; destination_url: string }>;
				removeAndReplaceAll: boolean;
			}) => {
				return {
					url: `rtb-deployment/deploy-new-ads`,
					method: "POST",
					body: {
						order_id: +orderId,
						files,
						remove_and_replace_all: removeAndReplaceAll ? "Y" : "N",
					},
				};
			},
		}),
		deleteFileByOrderId: builder.mutation({
			query: ({ orderId, fileId }: { orderId: string | undefined; fileId: string }) => ({
				url: `agency-order/${orderId}/files/${fileId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["orders"],
		}),
		requestInterimReport: builder.mutation({
			query: ({ orderId }: { orderId: string }) => ({
				url: `/agency-order/request-interim-report/${orderId}`,
				method: "POST",
			}),
			invalidatesTags: ["orders"],
		}),
		deleteOrder: builder.mutation({
			query: ({ orderId }: { orderId: string }) => ({
				url: `agency-order/${orderId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["orders"],
		}),
		addAssets: builder.mutation({
			query: ({ orderId, formData }) => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				// If formData is an object, append its keys to FormData
				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: `agency-order/order-setup/${orderId}/assets`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),
		addDeployment: builder.mutation({
			query: ({ orderId, formData }) => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				// If formData is an object, append its keys to FormData
				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: `agency-order/order-setup/${orderId}/deployments`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),

		saveEblastCampaignPdHtml: builder.mutation({
			query: formData => {
				console.log("formData", formData);
				return {
					url: `eblast/pd-html-loader/save-campaign`,
					method: "PUT",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: formData,
				};
			},
		}),

		bulkDestinationURL: builder.mutation({
			query: ({ orderId, formData }: { orderId: string; formData: any }) => {
				return {
					url: `agency-order/${orderId}/bulk-dest-url`,
					method: "PUT",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: formData,
				};
			},
		}),

		updateUniqueIO: builder.mutation({
			query: ({ orderId, formData }: { orderId: string; formData: any }) => {
				return {
					url: `agency-order/${orderId}/update-io`,
					method: "PUT",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: formData,
				};
			},
		}),

		deployToEblastTwo: builder.mutation({
			query: ({ order_id, file_id, formData }: { order_id: string; file_id: string; formData: any }) => {
				console.log("formData", formData);

				return {
					url: `eblast/pd-html-loader/deploy/eblast2/order/${order_id}/file/${file_id}`,
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: formData,
				};
			},
		}),

		deployCampaignLoader: builder.mutation({
			query: formData => {
				console.log("formData", formData);
				return {
					url: `/eblast/pd-html-loader/deploy/campaign-loader`,
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: formData,
				};
			},
		}),

		sendEblastSeed: builder.mutation({
			query: formData => {
				console.log("formData", formData);
				return {
					url: `/eblast/pd-html-loader/send/eblast-seed`,
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: formData,
				};
			},
		}),

		addTargeting: builder.mutation({
			query: ({ orderId, formData }) => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				// If formData is an object, append its keys to FormData
				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: `agency-order/order-setup/${orderId}/targeting`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),
		addTesting: builder.mutation({
			query: ({ orderId, formData }) => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				// If formData is an object, append its keys to FormData
				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: `agency-order/order-setup/${orderId}/testing`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),
		addFinal: builder.mutation({
			query: ({ orderId, formData }) => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				// If formData is an object, append its keys to FormData
				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: `agency-order/order-setup/${orderId}/final`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),
		// order setup query and mutations
		getOrderId: builder.query({
			query: () => ({
				url: `/agency-order/order-setup/next-order-id`,
			}),
		}),
		getProductPlans: builder.query<any, any>({
			query: ({ agencyId, agencyOnlyProducts }) => ({
				url: `/agency-order/order-setup/products?agency_id=${agencyId}&agency_only_products=${agencyOnlyProducts ? "Y" : "N"}&refresh=N`,
			}),
		}),

		getEBlastSeedTesting: builder.query<any, any>({
			query: ({ order_id, deployment_id, file_id }) => ({
				url: `/eblast/pd-html-loader/order/${order_id}/deployment/${deployment_id}/file/${file_id}`,
			}),
		}),

		getProductPlansById: builder.query<any, any>({
			query: ({ id }) => ({
				url: `/agency-order/order-setup/products/${id}`,
			}),
		}),
		getIndustrySpecific: builder.query<any, any>({
			query: ({ search }) => ({
				url: `/agency-order/order-setup/naics?search=${search}`,
			}),
			keepUnusedDataFor: 0, // cache time in seconds
		}),
		createOrder: builder.mutation({
			query: ({ orderId, formData }) => ({
				url: `/agency-order/order-setup/${orderId}/general`,
				method: "POST",
				body: buildFormDataPayload(formData),
			}),
			// invalidatesTags: ["orders"],
		}),
		addAutomatedBilling: builder.mutation<
			DefaultApiSuccessResponse,
			{ orderId: number | string; formData: AutomatedBillingPayload }
		>({
			query: ({ orderId, formData }) => ({
				url: `/agency-order/order-setup/${orderId}/automated-billing`,
				method: "POST",
				body: buildFormDataPayload(formData),
			}),
		}),
		getAdvancedReporting: builder.query<CampaignAdvancedReportResponse, { pdInternalId: string }>({
			query: ({ pdInternalId }) => ({
				url: `/agency-order/reporting/advanced/${pdInternalId}`,
			}),
		}),
		downloadAdvancedReportingPdf: builder.query<CampaignAdvancedReportPdfDownloadResponse, { pdInternalId: string }>({
			query: ({ pdInternalId }) => ({
				url: `/agency-order/reporting/advanced/download/${pdInternalId}/pdf`,
			}),
		}),

		downloadPreflightPDFReport: builder.query<Blob, { pdInternalCampaignId: string }>({
			query: ({ pdInternalCampaignId }) => ({
				url: `/dooh/audiences/preflight-report/pdf/download/${pdInternalCampaignId}`,
				method: "GET",
				responseHandler: response => response.blob(),
			}),
		}),

		sendNotification: builder.mutation({
			query: ({
				// to_user_id = "",
				is_response = false,
				released_by = "",
				order_id = "",
				topic_name = "__NOTIFICATIONS_ORDER_EDIT__",
				user_id = "",
				from_user_id = "",
				agency_id = "",
				is_admin = "N",
				company_name = "",
				user_name = "",
				notification_type = "broadcast",
				sender_name = "",
				force_admin_edit = "N",
			}) => {
				const formData = new FormData();
				formData.append("is_response", is_response ? "true" : "false");
				formData.append("released_by", released_by);
				formData.append("order_id", order_id);
				formData.append("topic_name", topic_name);
				formData.append("user_id", user_id);
				formData.append("agency_id", agency_id);
				formData.append("is_admin", is_admin);
				formData.append("company_name", company_name);
				formData.append("user_name", user_name);
				formData.append("sender_name", sender_name ?? user_name);
				formData.append("from_user", from_user_id);
				// formData.append("to_user", to_user_id);
				formData.append("notification_type", notification_type);
				formData.append("force_admin_edit", force_admin_edit);

				return {
					url: "/agency-order/order-setup/send-order-edit-notification",
					method: "POST",
					body: formData,
					keepalive: true,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),
		getTargetAudienceList: builder.query<any, any>({
			query: ({ search = "" }) => ({
				url: `traffic-estimator/list`,
				params: {
					search,
				},
			}),
		}),
		getOccupationTargetList: builder.query<any, any>({
			query: ({ search = "" }) => ({
				url: `agency-order/order-setup/occupation-target`,
				params: {
					search,
				},
			}),
		}),

		sendRenewalEmail: builder.mutation({
			query: ({ io }: { io: string }) => {
				return {
					url: `agency-order/notification/${io}/send-renewal-email`,
					method: "POST",
				};
			},
		}),

		sendCompletionEmail: builder.mutation({
			query: ({ io }: { io: string }) => {
				return {
					url: `agency-order/notification/${io}/send-completion-email`,
					method: "POST",
				};
			},
		}),

		updateOrderGeoInfo: builder.mutation({
			query: ({ orderId, formData }) => {
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: `agency-order/management-options/${orderId}/update-geo`,
					method: "POST",
					body: formDataPayload,
				};
			},
		}),

		getRtbDeploymentStatus: builder.query<RtbDeploymentStatusResponse, { orderId: string }>({
			query: ({ orderId }) => ({
				url: `agency-order/management-options/${orderId}/rtb-deployment-status`,
				method: "GET",
			}),
		}),

		bulkStatusChange: builder.mutation({
			query: formData => {
				return {
					url: "agency-order/notes/bulk-status-change",
					method: "POST",
					body: formData,
					headers: {
						accept: "application/json",
					},
				};
			},
			invalidatesTags: ["orders"],
		}),
	}),
});
// 298

export const {
	useGetOrderListQuery,
	useGetOrderByIdQuery,
	useGetNotesByOrderIDQuery,
	useFilesUploadMutation,
	useAddCopyCampaignMutation,
	useAddOrderNoteMutation,
	useDeleteOrderMutation,
	useAddAssetsMutation,
	useDeleteOrderNoteMutation,
	useDeleteFileByOrderIdMutation,
	useRtbAutomationMutation,
	useRtbDeploymentMutation,
	useDeployNewAdsMutation,
	useIsUniqueIOMutation,
	useGetProductByIdQuery,
	useLazyGetOrderIdQuery,
	useGetProductPlansQuery,
	useGetEBlastSeedTestingQuery,
	useAddDeploymentMutation,
	useGetIndustrySpecificQuery,
	useLazyGetIndustrySpecificQuery,
	useGetProductPlansByIdQuery,
	useLazyGetProductPlansByIdQuery,
	useAddFinalMutation,
	useAddTargetingMutation,
	useAddTestingMutation,
	useCreateOrderMutation,
	useAddAutomatedBillingMutation,
	useSaveEblastCampaignPdHtmlMutation,
	useUpdateUniqueIOMutation,
	useBulkDestinationURLMutation,
	useDeployToEblastTwoMutation,
	useDeployCampaignLoaderMutation,
	useSendEblastSeedMutation,
	useLazyGetMetaByIdQuery,
	useLazyGetAdvancedReportingQuery,
	useLazyDownloadAdvancedReportingPdfQuery,
	useLazyDownloadPreflightPDFReportQuery,
	useSendNotificationMutation,
	useRequestInterimReportMutation,
	useGetTargetAudienceListQuery,
	useLazyGetTargetAudienceListQuery,
	useGetOccupationTargetListQuery,
	useSendRenewalEmailMutation,
	useSendCompletionEmailMutation,
	useUpdateOrderGeoInfoMutation,
	useGetRtbDeploymentStatusQuery,
	useBulkStatusChangeMutation,
} = orderManagementSlice;
